import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import { Toaster } from "react-hot-toast";
import "./App.css";

// ============================================================
// PAGES
// ============================================================

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import DashboardReception from "./pages/DashboardReception";
import AdminDashboard from "./pages/AdminDashboard";

import AppartementPlan from "./pages/AppartementPlan";
import AppartementDetail from "./pages/AppartementDetail";
import HistoriqueAppartement from "./pages/HistoriqueAppartement";

import ClientsList from "./modules/clients/ClientsList";
import ClientForm from "./modules/clients/ClientForm";
import ClientEditForm from "./modules/clients/ClientEditForm";
import ClientsDeletedList from "./modules/clients/ClientsDeletedList";
import ClientDetail from "./modules/clients/ClientDetail";

import PersonnelList from "./modules/personnel/PersonnelList";
import PersonnelDetail from "./modules/personnel/PersonnelDetail";
import PersonnelForm from "./modules/personnel/PersonnelForm";
import PersonnelEditForm from "./modules/personnel/PersonnelEditForm";

import UsersPage from "./pages/UsersPage";

import RevenusList from "./modules/revenus/RevenusList";
import PaiementForm from "./pages/PaiementForm";

import DepensesList from "./pages/DepensesList";
import AjouterDepense from "./pages/AjouterDepense";
import SalairesList from "./pages/SalairesList";
import AuditLogsPage from "./pages/AuditLogsPage";

import ReservationDetails from "./modules/reservations/ReservationDetails";

import FacturePage from "./pages/FacturePage";
import FacturesList from "./pages/FacturesList";
import RecuPage from "./pages/RecuPage";
import RecusList from "./pages/RecusList";

import LogsAdminDashboard from "./pages/LogsAdminDashboard";
import SecurityDashboard from "./pages/SecurityDashboard";
import AuditDashboard from "./pages/AuditDashboard";

import MainLayout from "./layout/MainLayout";

import Bilan from "./pages/Bilan";
import Statistiques from "./pages/Statistiques";

import SejourDetail from "./pages/SejourDetail";

import { getUser } from "./utils/auth";

import SalairesHistorique from "./pages/SalairesHistorique";

import SejoursList from "./pages/SejoursList";

import ReceptionJournee from "./pages/ReceptionJournee";

import Remboursements from "./pages/Remboursements";

import PublicSearch from "./publicPages/PublicSearch";

import OtaSyncDashboard from "./pages/OtaSyncDashboard";

import RevenueMultiChannelDashboard from "./pages/RevenueMultiChannelDashboard";

import PendingBookings from "./pages/PendingBookings";

import Immeubles from "./pages/Immeubles";
import ImmeubleDetail from "./pages/ImmeubleDetail";

import VerificationFacture from "./pages/VerificationFacture";

import ProformasList from "./modules/proformas/ProformasList";
import ProformaForm from "./modules/proformas/ProformaForm";
import ProformaEditForm from "./modules/proformas/ProformaEditForm";

import ReservationList from "./modules/reservations/ReservationList";

// ============================================================
// PRESTATIONS
// ============================================================

import Blanchisserie from "./pages/Blanchisserie";
import BlanchisserieEncaissement from "./pages/BlanchisserieEncaissement";
import BlanchisserieDetail from "./pages/BlanchisserieDetail";
import VerificationBlanchisserie from "./publicPages/VerificationBlanchisserie";
import BlanchisseriePrint from "./pages/BlanchisseriePrint";

import PrestataireDashboard
    from "./pages/PrestataireDashboard";

import PrestationsGestion
    from "./pages/PrestationsGestion";

import PrestationsRecusList
    from "./pages/PrestationsRecusList";

// ============================================================
// PROTECTION
// ============================================================

const PrivateRoute = ({ children }) => {
  const user = getUser();

  return user
    ? children
    : <Navigate to="/" replace />;
};

// ============================================================
// ADMIN UNIQUEMENT
// ============================================================

const AdminOnly = ({ children }) => {
  const user = getUser();

  return user?.role === "admin"
    ? children
    : <Navigate to="/dashboard" replace />;
};

// ============================================================
// COMPTABLE UNIQUEMENT
// ============================================================

const ComptableOnly = ({ children }) => {
  const user = getUser();

  return user?.role === "comptable"
    ? children
    : <Navigate to="/dashboard" replace />;
};

// ============================================================
// PRESTATAIRE UNIQUEMENT
// ============================================================

const PrestataireOnly = ({ children }) => {
  const user = getUser();

  return user?.role === "prestataire"
    ? children
    : <Navigate to="/dashboard" replace />;
};

// ============================================================
// DEBUG PROFORMA — TEMPORAIRE
// ============================================================

class ProformaErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      info: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, info) {
    console.error("🔥 PROFORMA ERROR:", error);
    console.error("🔥 PROFORMA ERROR INFO:", info);

    this.setState({
      error,
      info
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          padding: "40px",
          fontFamily: "Arial, sans-serif",
          background: "#fff",
          color: "#222",
          minHeight: "100vh"
        }}
      >
        <h1 style={{ color: "#c00" }}>
          Erreur dans la page Proforma
        </h1>

        <h3>Message :</h3>

        <pre
          style={{
            background: "#f5f5f5",
            padding: "20px",
            whiteSpace: "pre-wrap",
            border: "1px solid #ddd"
          }}
        >
          {this.state.error?.toString()}
        </pre>

        <h3>Détails :</h3>

        <pre
          style={{
            background: "#f5f5f5",
            padding: "20px",
            whiteSpace: "pre-wrap",
            border: "1px solid #ddd",
            maxHeight: "400px",
            overflow: "auto"
          }}
        >
          {this.state.info?.componentStack}
        </pre>
      </div>
    );
  }
}

// ============================================================
// RÔLES AUTORISÉS POUR LES PRESTATIONS
// ============================================================
//
// Réceptionniste : ancien workflow des prestations
// Comptable      : gestion / consultation
// Prestataire    : Café & Bar
// Admin          : accès complet
//

const PrestationsAccess = ({ children }) => {
  const user = getUser();

  const allowedRoles = [
    "admin",
    "comptable",
    "receptionniste",
    "prestataire"
  ];

  return allowedRoles.includes(user?.role)
    ? children
    : <Navigate to="/dashboard" replace />;
};

const CataloguePrestationsAccess = ({
    children
}) => {
    const user = getUser();

    const allowedRoles = [
        "admin",
        "comptable"
    ];

    return allowedRoles.includes(
        user?.role
    )
        ? children
        : <Navigate
            to="/dashboard"
            replace
          />;
};

// ============================================================
// DASHBOARD INTELLIGENT
// ============================================================

const SmartDashboard = () => {
  const user = getUser();

  if (user?.role === "admin") {
    return (
      <Navigate
        to="/admindashboard"
        replace
      />
    );
  }

  if (user?.role === "receptionniste") {
    return <DashboardReception />;
  }

  // ----------------------------------------------------------
  // PRESTATAIRE
  // ----------------------------------------------------------

  if (user?.role === "prestataire") {
    return <PrestataireDashboard />;
  }

  return <Dashboard />;
};

// ============================================================
// APP
// ============================================================

function App() {
  return (
    <>
      <Toaster
        position="top-right"
      />

      <Routes>

        {/* ==================================================
            PUBLIC
        ================================================== */}

        <Route
          path="/"
          element={<Login />}
        />

        <Route
          path="/reservation"
          element={<PublicSearch />}
        />

        <Route
          path="/verification/:numero"
          element={<VerificationFacture />}
        />

        <Route
          path="/verification/blanchisserie/:numero"
          element={<VerificationBlanchisserie />}
        />

        {/* ==================================================
            PROTECTED
        ================================================== */}

        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >

          {/* ==================================================
              DASHBOARD
          ================================================== */}

          <Route
            path="/dashboard"
            element={<SmartDashboard />}
          />

          <Route
            path="/dashboard-reception"
            element={<DashboardReception />}
          />

          {/* ==================================================
              ADMIN
          ================================================== */}

          <Route
            path="/admindashboard"
            element={
              <AdminOnly>
                <AdminDashboard />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/users"
            element={
              <AdminOnly>
                <UsersPage />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/logs"
            element={
              <AdminOnly>
                <LogsAdminDashboard />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/security"
            element={
              <AdminOnly>
                <SecurityDashboard />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/audit"
            element={
              <AdminOnly>
                <AuditDashboard />
              </AdminOnly>
            }
          />

          <Route
            path="/admin/ota-sync"
            element={
              <AdminOnly>
                <OtaSyncDashboard />
              </AdminOnly>
            }
          />

          {/* ==================================================
              CLIENTS
          ================================================== */}

          <Route
            path="/clients"
            element={<ClientsList />}
          />

          <Route
            path="/clients/new"
            element={<ClientForm />}
          />

          <Route
            path="/clients/:id"
            element={<ClientDetail />}
          />

          <Route
            path="/clients/:id/edit"
            element={<ClientEditForm />}
          />

          <Route
            path="/clients/deleted"
            element={<ClientsDeletedList />}
          />

          {/* ==================================================
              APPARTEMENTS
          ================================================== */}

          <Route
            path="/plan"
            element={<AppartementPlan />}
          />

          <Route
            path="/appartement"
            element={<AppartementDetail />}
          />

          <Route
            path="/appartement/:id/historique"
            element={<HistoriqueAppartement />}
          />

          {/* ==================================================
              PERSONNEL
          ================================================== */}

          <Route
            path="/personnel"
            element={<PersonnelList />}
          />

          <Route
            path="/personnel/new"
            element={<PersonnelForm />}
          />

          <Route
            path="/personnel/:id"
            element={<PersonnelDetail />}
          />

          <Route
            path="/personnel/:id/edit"
            element={<PersonnelEditForm />}
          />

          {/* ==================================================
              REVENUS
          ================================================== */}

          <Route
            path="/revenus"
            element={<RevenusList />}
          />

          <Route
            path="/statistiques/revenue-channels"
            element={<RevenueMultiChannelDashboard />}
          />

          {/* ==================================================
              PRESTATIONS
          ================================================== */}

          <Route
            path="/blanchisserie"
            element={
              <PrestationsAccess>
                <Blanchisserie />
              </PrestationsAccess>
            }
          />

          <Route
            path="/blanchisserie/nouveau"
            element={
              <PrestationsAccess>
                <BlanchisserieEncaissement />
              </PrestationsAccess>
            }
          />

          <Route
            path="/blanchisserie/recus"
            element={
              <PrestationsAccess>
                <PrestationsRecusList />
              </PrestationsAccess>
            }
          />

          <Route
            path="/blanchisserie/:id"
            element={
              <PrestationsAccess>
                <BlanchisserieDetail />
              </PrestationsAccess>
            }
          />

          <Route
            path="/blanchisserie/:id/impression"
            element={
              <PrestationsAccess>
                <BlanchisseriePrint />
              </PrestationsAccess>
            }
          />

          <Route
              path="/prestations/catalogue"
              element={
                  <CataloguePrestationsAccess>
                      <PrestationsGestion />
                  </CataloguePrestationsAccess>
              }
          />

          {/* ==================================================
              PAIEMENTS / DÉPENSES
          ================================================== */}

          <Route
            path="/paiements"
            element={<PaiementForm />}
          />

          <Route
            path="/depenses"
            element={<DepensesList />}
          />

          <Route
            path="/depenses/ajouter"
            element={<AjouterDepense />}
          />

          <Route
            path="/bilan"
            element={<Bilan />}
          />

          <Route
            path="/statistiques"
            element={<Statistiques />}
          />

          {/* ==================================================
              SALAIRES
          ================================================== */}

          <Route
            path="/salaires"
            element={<SalairesList />}
          />

          <Route
            path="/salaires/historique"
            element={<SalairesHistorique />}
          />

          {/* ==================================================
              RÉSERVATIONS
          ================================================== */}

          <Route
            path="/reservations"
            element={<ReservationList />}
          />

          <Route
            path="/reservations/:id"
            element={<ReservationDetails />}
          />

          {/* ==================================================
              FACTURES
          ================================================== */}

          <Route
            path="/factures"
            element={<FacturesList />}
          />

          <Route
            path="/facture/:id"
            element={<FacturePage />}
          />

          <Route
            path="/recus"
            element={<RecusList />}
          />

          <Route
            path="/recu/:id"
            element={<RecuPage />}
          />

          {/* ==================================================
              SÉJOURS
          ================================================== */}

          <Route
            path="/sejours"
            element={<SejoursList />}
          />

          <Route
            path="/sejours/:id"
            element={<SejourDetail />}
          />

          <Route
            path="/remboursements"
            element={<Remboursements />}
          />

          {/* ==================================================
              AUTRES
          ================================================== */}

          <Route
            path="/audit-logs"
            element={<AuditLogsPage />}
          />

          <Route
            path="/payment/pending-bookings"
            element={<PendingBookings />}
          />

          <Route
            path="/immeubles"
            element={<Immeubles />}
          />

          <Route
            path="/immeubles/:id"
            element={<ImmeubleDetail />}
          />

          {/* ==================================================
              PROFORMAS
          ================================================== */}

          <Route
            path="/proformas"
            element={<ProformasList />}
          />

          <Route
            path="/proformas/new"
            element={
              <ProformaErrorBoundary>
                <ProformaForm />
              </ProformaErrorBoundary>
            }
          />

          <Route
            path="/proformas/:id/edit"
            element={
              <ProformaErrorBoundary>
                <ProformaEditForm />
              </ProformaErrorBoundary>
            }
          />

        </Route>

        {/* ==================================================
            FALLBACK
        ================================================== */}

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />

      </Routes>
    </>
  );
}

export default App;